import { GridApiClient} from "../api/grid-api-client";
import {
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
  RequestKycLinkRequest,
  RequestVirtualAccountRequest,
  GetVirtualAccountsQueryParams,
  CreateStandingOrderRequest,
  CreateStandingOrderResponse,
  GetStandingOrdersQueryParams,
  KycLinkResponse,
} from "../types";
import { VirtualAccount, StandingOrder, KycStatusResponse, StandingOrderResponse, StandingOrdersResponse, VirtualAccountResponse, VirtualAccountsResponse } from "../types/common";

/**
 * @hidden
 * Service handling banking operations (payment intents, KYC, virtual accounts, standing orders)
 */
export class BankingService {
  private apiClient: GridApiClient;

  constructor(apiClient: GridApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Initiates a payment intent that allows users to deposit fiat currency
   * and receive cryptocurrency in their account. Handles the complete
   * fiat-to-crypto conversion process.
   *
   * @param accountAddress - The account address to receive the converted crypto
   * @param request - Payment intent request with amount, currency, and payment method
   * @returns Promise resolving to payment intent response with transaction details
   *
   * @example
   * ```typescript
   * try {
   *   const paymentIntent = await gridClient.createPaymentIntent(accountAddress, {
   *     amount: "100000",
   *     source: {
   *       account: accountAddress,
   *       currency: "usdc"
   *     },
   *     destination: {
   *       address: "3ciascNndLTBrDQXvs8nzZgWAiaL33tGM6fx3zzM7Fxt",
   *       currency: "usdc"
   *     }
   *   });
   *   console.log(paymentIntent.paymentUrl);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Payment intent failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Payment Processing
   */
  async createPaymentIntent(
    accountAddress: string,
    request: CreatePaymentIntentRequest
  ): Promise<CreatePaymentIntentResponse> {
    const response = await this.apiClient.createPaymentIntent(
      accountAddress,
      request
    );

    // Response is already transformed to camelCase by API client
    const apiData = response.data;

    const transactionPayload = {
      kmsPayloads: apiData.kmsPayloads,
      transactionSigners: apiData.transactionSigners,
      transaction: apiData.transaction,
    };

    // Return response with excluded fields removed and transaction data wrapped
    const {
      kmsPayloads,
      transactionSigners,
      transaction,
      ...cleanedResponse
    } = apiData;

    return {
      data: {
        ...cleanedResponse,
        transactionPayload,
      },
      lastResponse: response.lastResponse
    };
  }

  /**
   * Generates a link for Know Your Customer (KYC) identity verification.
   * Users complete the verification process through the provided link.
   *
   * @param accountAddress - The account address to associate with KYC
   * @param request - KYC link request with verification requirements
   * @returns Promise resolving to KYC link with verification URL
   *
   * @example
   * ```typescript
   * try {
   *   const kycLink = await gridClient.requestKycLink(accountAddress, {
   *     type: 'individual',
   *     email: 'user@example.com',
   *     fullName: 'John Doe',
   *     endorsements: [],
   *     redirectUri: 'your-redirect-uri'
   *   });
   *   console.log('KYC URL:', kycLink.link);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('KYC link generation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category KYC Verification
   */
  async requestKycLink(
    accountAddress: string,
    request: RequestKycLinkRequest
  ): Promise<KycLinkResponse> {
    const response = await this.apiClient.requestKycLink(
      accountAddress,
      request
    );
    return response;
  }

  /**
   * Get KYC verification status
   *
   * Retrieves the current status of a KYC verification process, including
   * completion status, verification level, and any required actions.
   *
   * @param accountAddress - The account address associated with KYC
   * @param kycId - The KYC verification identifier
   * @returns Promise resolving to KYC status data
   *
   * @example
   * ```typescript
   * try {
   *   const kycStatus = await gridClient.getKycStatus(accountAddress, kycId);
   *   console.log('Status:', kycStatus.status);
   *   console.log('Level:', kycStatus.verificationLevel);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get KYC status:', error.message);
   *   }
   * }
   * ```
   *
   * @category KYC Verification
   */
  async getKycStatus(
    accountAddress: string,
    kycId: string
  ): Promise<KycStatusResponse> {
    const response = await this.apiClient.getKycStatus(accountAddress, kycId);
    return response;
  }

  /**
   * Creates a virtual bank account that allows users to receive fiat deposits
   * which are automatically converted to cryptocurrency in their Grid account.
   *
   * @param accountAddress - The account address to associate with the virtual account
   * @param request - Virtual account request with banking details and preferences
   * @returns Promise resolving to virtual account with banking details
   *
   * @example
   * ```typescript
   * try {
   *   const virtualAccount = await gridClient.requestVirtualAccount(accountAddress, {
   *     currency: 'usd' as const
   *   });
   *   console.log('Account number:', virtualAccount.accountNumber);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Virtual account creation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Virtual Accounts
   */
  async requestVirtualAccount(
    accountAddress: string,
    request: RequestVirtualAccountRequest
  ): Promise<VirtualAccountResponse> {
    const response = await this.apiClient.requestVirtualAccount(
      accountAddress,
      request
    );
    return response;
  }

  /**
   * Retrieves all virtual bank accounts associated with a Grid account,
   * including their status, balances, and transaction history.
   *
   * @param accountAddress - The account address to get virtual accounts for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to array of virtual accounts
   *
   * @example
   * ```typescript
   * try {
   *   const virtualAccounts = await gridClient.getVirtualAccounts(accountAddress);
   *   virtualAccounts.forEach(account => {
   *     console.log('Account:', account.id);
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get virtual accounts:', error.message);
   *   }
   * }
   * ```
   *
   * @category Virtual Accounts
   */
  async getVirtualAccounts(
    accountAddress: string,
    queryParams?: GetVirtualAccountsQueryParams
  ): Promise<VirtualAccountsResponse> {
    const response = await this.apiClient.getVirtualAccounts(
      accountAddress,
      queryParams
    );
    return response;
  }

  /**
   * Create a standing order.
   *
   * @param accountAddress - The account address that will make the recurring payments
   * @param request - Standing order request with payment details and schedule
   * @returns Promise resolving to standing order creation response
   *
   * @example
   * ```typescript
   * try {
   *   const standingOrder = await gridClient.createStandingOrder(accountAddress, {
   *     amount: "500000",
   *     source: {
   *       account: "source-address",
   *       currency: "usdc"
   *     },
   *     destination: {
   *       address: "destination-address",
   *       currency: "usdc"
   *     },
   *     frequency: "weekly",
   *     startDate: "2024-12-02T10:00:00Z",
   *     endDate: "2025-12-31T23:59:59Z"
   *   });
   *   console.log('Standing order created:', standingOrder.id);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Standing order creation failed:', error.message);
   *   }
   * }
   * ```
   *
   * @category Standing Orders
   */
  async createStandingOrder(
    accountAddress: string,
    request: CreateStandingOrderRequest
  ): Promise<CreateStandingOrderResponse> {
    const response = await this.apiClient.createStandingOrder(
      accountAddress,
      request
    );
    return response;
  }

  /**
   * Retrieves all standing orders associated with an account, including
   * active, paused, and completed recurring payment schedules.
   *
   * @param accountAddress - The account address to get standing orders for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to array of standing orders
   *
   * @example
   * ```typescript
   * try {
   *   const standingOrders = await gridClient.getStandingOrders(accountAddress);
   *   standingOrders.forEach(order => {
   *     console.log('Order:', order.id, 'Status:', order.status);
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get standing orders:', error.message);
   *   }
   * }
   * ```
   *
   * @category Standing Orders
   */
  async getStandingOrders(
    accountAddress: string,
    queryParams?: GetStandingOrdersQueryParams
  ): Promise<StandingOrdersResponse> {
    const response = await this.apiClient.getStandingOrders(
      accountAddress,
      queryParams
    );
    return response;
  }

  /**
   * Retrieves detailed information about a specific standing order,
   * including its current status, execution history, and next payment date.
   *
   * @param accountAddress - The account address that owns the standing order
   * @param standingOrderId - The unique identifier of the standing order
   * @returns Promise resolving to standing order details
   *
   * @example
   * ```typescript
   * try {
   *   const order = await gridClient.getStandingOrder(accountAddress, orderId);
   *   console.log('Next payment:', order.nextPaymentDate);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get standing order:', error.message);
   *   }
   * }
   * ```
   *
   * @category Standing Orders
   */
  async getStandingOrder(
    accountAddress: string,
    standingOrderId: string
  ): Promise<StandingOrderResponse> {
    const response = await this.apiClient.getStandingOrder(
      accountAddress,
      standingOrderId
    );
    return response;
  }

}